import type { PlaceholderValue } from "@homeinstead/schemas";
import type { PortableTextBlock } from "@portabletext/types";

import { isEmptyValue } from "@homeinstead/schemas";

type DynamicPlaceholderMarkDef = {
  _key: string;
  _type: "dynamicPlaceholder";
  placeholderKey?: string;
  hideWhenEmpty?: boolean;
};

type TextSpan = {
  text: string;
  marks: string[];
};

/**
 * Extracts text and marks from a block child if it's a valid text span.
 */
function extractTextSpan(child: unknown): TextSpan | null {
  if (typeof child !== "object" || child === null || !("text" in child)) {
    return null;
  }

  const childObj = child as { text?: unknown; marks?: string[] };
  return {
    marks: childObj.marks || [],
    text: typeof childObj.text === "string" ? childObj.text.trim() : "",
  };
}

/**
 * Checks if a span makes the block visible (i.e., block should NOT be hidden).
 */
function isSpanVisible(
  span: TextSpan,
  hideWhenEmptyPlaceholders: Map<string, string>,
  placeholders: Record<string, PlaceholderValue>,
): boolean {
  const { text, marks } = span;

  // Non-empty static text without marks is always visible
  if (text && marks.length === 0) {
    return true;
  }

  // Text with marks that aren't hideWhenEmpty placeholders is visible
  if (text && !marks.some((mark) => hideWhenEmptyPlaceholders.has(mark))) {
    return true;
  }

  // Check if any hideWhenEmpty placeholder has a non-empty value
  return marks.some((mark) => {
    const placeholderKey = hideWhenEmptyPlaceholders.get(mark);
    return (
      placeholderKey !== undefined &&
      !isEmptyValue(placeholders[placeholderKey])
    );
  });
}

/**
 * Checks if a block will be completely hidden due to hideWhenEmpty placeholders.
 * A block is hidden if:
 * 1. It has no static text content (or only whitespace)
 * 2. All children with marks reference hideWhenEmpty placeholders
 * 3. All those placeholder values are empty
 */
function isBlockHiddenByPlaceholders(
  block: PortableTextBlock,
  placeholders: Record<string, PlaceholderValue>,
): boolean {
  if (block._type !== "block" || !block.children) {
    return false;
  }

  const markDefs = (block.markDefs || []) as DynamicPlaceholderMarkDef[];
  const hideWhenEmptyPlaceholders = new Map(
    markDefs
      .filter(
        (markDef) =>
          markDef._type === "dynamicPlaceholder" && markDef.hideWhenEmpty,
      )
      .map((markDef) => [markDef._key, markDef.placeholderKey || ""]),
  );

  if (hideWhenEmptyPlaceholders.size === 0) {
    return false;
  }

  const textSpans = block.children
    .map(extractTextSpan)
    .filter((span): span is TextSpan => span !== null);

  const hasVisibleContent = textSpans.some((span) =>
    isSpanVisible(span, hideWhenEmptyPlaceholders, placeholders),
  );

  return !hasVisibleContent;
}

/**
 * Extracts plain text from Portable Text blocks.
 * Useful for rendering placeholder content in inline contexts where
 * block-level elements would cause invalid HTML nesting.
 *
 * @param blocks - Array of Portable Text blocks
 * @returns Plain text string
 */
function extractPlainTextFromBlocks(blocks: PortableTextBlock[]): string {
  return blocks
    .filter((block): block is PortableTextBlock => block._type === "block")
    .flatMap((block) => {
      if (!Array.isArray(block.children)) {
        return [];
      }

      return block.children
        .filter(
          (
            child,
          ): child is PortableTextBlock["children"][number] & {
            text: string;
          } =>
            typeof child === "object" &&
            "text" in child &&
            typeof child.text === "string",
        )
        .map((child) => child.text);
    })
    .join(" ");
}

/**
 * Filters out empty blocks from a Portable Text array.
 * Empty blocks are those with _type "block" that contain only whitespace text.
 *
 * @param blocks - Array of Portable Text blocks
 * @returns Filtered array with empty blocks removed
 */
function filterEmptyBlocks(blocks: PortableTextBlock[]): PortableTextBlock[] {
  return blocks.filter((block) => {
    // Keep non-block types (images, custom blocks, etc.)
    if (block._type !== "block" || !block.children) {
      return true;
    }

    // Extract text content from all children
    const text = block.children
      .map((child) =>
        typeof child === "object" &&
        "text" in child &&
        typeof child.text === "string"
          ? child.text
          : "",
      )
      .join("")
      .trim();

    // Keep blocks that have non-empty text
    return text !== "";
  });
}

/**
 * Filters out blocks that will be completely hidden due to hideWhenEmpty placeholders.
 * This prevents rendering <br/> for blocks where all content is hidden.
 *
 * @param blocks - Array of Portable Text blocks
 * @param placeholders - Map of placeholder keys to their values
 * @returns Filtered array with hidden blocks removed
 */
function filterHiddenPlaceholderBlocks(
  blocks: PortableTextBlock[],
  placeholders: Record<string, PlaceholderValue>,
): PortableTextBlock[] {
  return blocks.filter(
    (block) => !isBlockHiddenByPlaceholders(block, placeholders),
  );
}

/**
 * Checks if an array is valid Portable Text (has proper block structure).
 * Valid Portable Text blocks must have a _type property.
 *
 * @param value - Array to check
 * @returns true if the array contains valid Portable Text blocks
 */
function isValidPortableText(value: unknown[]): boolean {
  if (value.length === 0) {
    return false;
  }

  return value.every(
    (item) =>
      typeof item === "object" &&
      item !== null &&
      "_type" in item &&
      typeof (item as { _type: unknown })._type === "string",
  );
}

export {
  extractPlainTextFromBlocks,
  filterEmptyBlocks,
  filterHiddenPlaceholderBlocks,
  isValidPortableText,
};
